import { CustomCheckbox, CustomDatePicker } from "@components/Forms";
import { DropIcon } from "@components/Icons";
import Breadcrumb from "@components/UI/Breadcrumb";
import SelectVerificationReportVersionModal from "@components/UI/SelectVerificationReportVersionModal";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { OutlineButton, PrimaryButton } from "@components/Buttons";

const AddNoticeInformation = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    verificationReportSeq: "",
    projectId: "",
    noticeInfoWriteStatusCd: "REQUEST",
    writeCompletionExpectedDt: null,
  });
  const [selectedVerificationReport, setSelectedVerificationReport] = useState(null);
  const [showSelectModal, setShowSelectModal] = useState(false);

  const breadcrumbItems = [
    { label: t("project.notice-infor.notice-infor-list"), href: "/notice-information" },
    { label: t("project.notice-infor.add-notice"), href: "/notice-information/new" },
  ];

  // Create notice info mutation (same pattern as useVerificationReportDetail)
  const createNoticeInfoMutation = useMutationData(
    "/notice_info",
    { method: "POST" },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.NOTICE_INFOS, response, response.noticeInfoSeq);
        navigate(`/notice-information/${response.noticeInfoSeq}`);
      },
      onError: (error) => {
        ToastConstant(TOAST.error, t("common.error"), t("project.notice-infor.add-error"));
        console.error("Error creating notice info:", error);
      },
    },
  );

  const isSubmitting = createNoticeInfoMutation.isLoading;
  const hasSelectedWriteType =
    formData.noticeInfoWriteStatusCd === "REQUEST";

  // Handle create notice info (same logic as RequestNoticeModal)
  const handleCreateNoticeInfo = (requestData) => {
    createNoticeInfoMutation.mutate({
      verificationReportSeq: formData.verificationReportSeq,
      projectId: formData.projectId,
      noticeInfoWriteStatusCd: requestData.noticeInfoWriteStatusCd,
      writeCompletionExpectedDt: requestData.writeCompletionExpectedDt,
    });
  };

  const handleCancel = () => {
    navigate("/notice-information");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVerificationReportSelect = (report) => {
    setSelectedVerificationReport(report);
    setFormData((prev) => ({
      ...prev,
      verificationReportSeq: report.verificationReportSeq,
      projectId: report.projectId || "",
    }));
  };

  const handleOpenSelectModal = () => {
    setShowSelectModal(true);
  };

  const handleCloseSelectModal = () => {
    setShowSelectModal(false);
  };

  const handleCheckboxChange = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field] === "REQUEST" ? "UNNECESSARY" : "REQUEST",
    }));
  };

  const handleSubmit = () => {
    // Validate verification report selection
    if (!formData.verificationReportSeq) {
      ToastConstant(TOAST.error, t("common.please-select-verification-report"));
      return;
    }

    // Validate that checkbox is selected
    if (!hasSelectedWriteType) {
      ToastConstant(TOAST.error, t("common.please-select-write-type"));
      return;
    }

    // Validate date if provided
    if (formData.writeCompletionExpectedDt) {
      const selectedDate = new Date(formData.writeCompletionExpectedDt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        ToastConstant(TOAST.error, t("common.completion-date-cannot-be-past"));
        return;
      }
    }

    // Prepare form data for handleCreateNoticeInfo (same format as RequestNoticeModal)
    const noticeFormData = {
      noticeInfoWriteStatusCd: formData.noticeInfoWriteStatusCd,
      writeCompletionExpectedDt: formData.writeCompletionExpectedDt,
    };

    handleCreateNoticeInfo(noticeFormData);
  };

  return (
    <div className={`flex h-full flex-col ${colors.bgSecondary} p-6`} data-testid="add-notice-information-page">
      {/* Header */}
      <div className={`${colors.bgSecondary}`} data-testid="add-notice-information-header">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Breadcrumb data-testid="add-notice-information-breadcrumb" items={breadcrumbItems} />
            <h1
              className={`${colors.textPrimary} text-2xl font-bold uppercase`}
              data-testid="add-notice-information-page-title"
            >
              {t("project.notice-infor.add-notice")}
            </h1>
          </div>
          <div className="flex items-center space-x-3" data-testid="add-notice-information-header-actions">
            <OutlineButton data-testid="add-notice-information-cancel-button" onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
            <PrimaryButton data-testid="add-notice-information-save-button" onClick={handleSubmit} disabled={!formData.verificationReportSeq || !hasSelectedWriteType || isSubmitting}>
              {t("common.save-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="add-notice-information-form">
        <div className="mx-auto w-full max-w-[55rem]">
          {/* Notice Information Title */}
          <div className="mb-6" data-testid="add-notice-information-section">
            <h2
              className={`${colors.textPrimary} text-xl font-semibold mb-2 uppercase`}
              data-testid="add-notice-information-section-title"
            >
              {t("project.notice-infor.notice-information-title")}
            </h2>
            <p className={`mb-4 text-sm ${colors.textSubTitle}`}>
              {t("project.required-fields-note")} <span className={`${colors.textRequired}`}>*</span>
            </p>
          </div>

          {/* SCAN TYPE Section */}
          {/* <div className="mb-6">
            <div className={`${colors.bgVerifReportFile || colors.bgPrimary} ${colors.borderPrimary} border rounded-lg p-4`}>
              <h3 className={`${colors.textPrimary} text-lg font-semibold mb-2`}>
                {t("type")}<span className={`${colors.textRequired}`}> *</span>
              </h3>

              <div>
                <CustomCheckbox
                  id="noticeInfoWriteStatusCd"
                  checked={formData.noticeInfoWriteStatusCd === "REQUEST"}
                  onChange={() => handleCheckboxChange("noticeInfoWriteStatusCd")}
                  label={t("project.verification-report.notice-write")}
                  disabled={false}
                  size="md"
                />
              </div>
            </div>
          </div> */}

          {/* WRITE COMPLETION EXPECTED DATE Section */}
          <div className="mb-6">
            <h2 className={`${colors.textPrimary} font-semibold mb-2 up uppercase`}>
              {t("project.verification-report.write-completion-expected-date")}
            </h2>

            <div className="w-1/3">
              <CustomDatePicker
                data-testid="add-notice-information-completion-date-picker"
                selected={formData.writeCompletionExpectedDt ? new Date(formData.writeCompletionExpectedDt) : null}
                onChange={(date) => handleInputChange("writeCompletionExpectedDt", date)}
                placeholderText={t("project.select-date-placeholder")}
                dateFormat="yyyy-MM-dd"
                minDate={new Date()}
                size="sm"
              />
            </div>
          </div>

          {/* Verification Report Selection */}
          <div className="mb-6">
            <h2 className={`${colors.textPrimary} font-semibold mb-2 uppercase`}>
              {t("project.verification-report.verification-report")}
              <span className={`${colors.textRequired}`}> *</span>
            </h2>

            {/* Verification Report Information Panel */}
            <div className={`${colors.bgVerifReportFile || colors.bgPrimary} ${colors.borderPrimary} border rounded-lg p-4`}>
              {!selectedVerificationReport ? (
                // Empty State
                <>
                  {/* Table Headers */}
                  <div className="grid grid-cols-4 gap-4 mb-4 px-4 py-2">
                    <div className={`${colors.textSecondary} text-sm font-medium`}>
                      {t("project.verification-report.verification-report-id")}
                    </div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>
                      {t("project.verification-report.verification-report-name")}
                    </div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>
                      {t("project.verification-report.verification-tool")}
                    </div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>
                      {t("project.verification-report.verif-report-version")}
                    </div>
                  </div>
                  
                  {/* Empty State Content */}
                  <div className="my-8 flex flex-col items-center justify-center space-y-4">
                    <div className="flex items-center justify-center">
                      <DropIcon size={100} className={`${colors.textActive}`} />
                    </div>
                    <p className={`${colors.textSecondary} text-sm`}>
                      {t("project.notice-infor.no-verification-report-information", "There's no verification report information to display.")}
                    </p>
                  </div>
                  
                  {/* Add Button */}
                  <div className="mt-4 flex justify-center">
                    <button
                      data-testid="add-notice-information-select-verification-report-button"
                      onClick={handleOpenSelectModal}
                      className={`px-6 py-2 text-sm font-semibold ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} ${colors.textButton} rounded-lg`}
                    >
                      {t("project.notice-infor.select-verification-report-version", "Add Verification Report Version")}
                    </button>
                  </div>
                </>
              ) : (
                // Table with Selected Report
                <>
                  {/* Table Headers */}
                  <div className="grid grid-cols-4 gap-4 mb-4 px-4 py-2">
                    <div className={`${colors.textSecondary} text-sm font-medium`}>
                      {t("project.verification-report.verification-report-id")}
                    </div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>
                      {t("project.verification-report.verification-report-name")}
                    </div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>
                      {t("project.verification-report.verification-tool")}
                    </div>
                    <div className={`${colors.textSecondary} text-sm font-medium`}>
                      {t("project.verification-report.verif-report-version")}
                    </div>
                  </div>
                  
                  {/* Table Row */}
                  <div className="grid grid-cols-4 gap-4 px-4 py-2">
                    <div className={`${colors.textPrimary} text-sm truncate`} title={selectedVerificationReport.verificationReportId || "-"}>
                      {selectedVerificationReport.verificationReportId || "-"}
                    </div>
                    <div className={`${colors.textPrimary} text-sm truncate`} title={selectedVerificationReport.verificationReportName || "-"}>
                      {selectedVerificationReport.verificationReportName || "-"}
                    </div>
                    <div className={`${colors.textPrimary} text-sm`}>
                      {selectedVerificationReport.verificationToolCd || "-"}
                    </div>
                    <div className={`${colors.textPrimary} text-sm`}>
                      {selectedVerificationReport.verificationReportVersion || selectedVerificationReport.verificationVersion || "-"}
                    </div>
                  </div>
                  
                  {/* Change Button */}
                  <div className="mt-4 flex justify-center">
                    <button
                      data-testid="add-notice-information-select-verification-report-button"
                      onClick={handleOpenSelectModal}
                      className={`px-6 py-2 text-sm font-semibold ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} ${colors.textButton} rounded-lg`}
                    >
                      {t("project.notice-infor.select-verification-report-version", "Add Verification Report Version")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Select Verification Report Version Modal */}
      <SelectVerificationReportVersionModal
        isOpen={showSelectModal}
        onClose={handleCloseSelectModal}
        onSelect={handleVerificationReportSelect}
        selectedVerificationReportSeq={formData.verificationReportSeq}
        managedProjectsOnly
      />
    </div>
  );
};

export default AddNoticeInformation;
